/**
 * cwFLOW widget entry — UMD bundle, **true DOM mount** (no iframe).
 *
 *   <script src="https://cdn.cwflow.io/widget.js"></script>
 *   <script>cwflow.mount('#cwflow-here', { tenant: 'lumi' })</script>
 *
 * Implementation notes:
 *   - React/ReactDOM are bundled in (`vite.widget.config.ts`).
 *   - Branding lives under a scoped `.cwflow-root` element via
 *     `BrandingProvider`. We additionally inject a tiny CSS reset that
 *     applies *only* under that scope, so a host page's resets don't
 *     bleed in (and ours don't bleed out).
 *   - The widget calls `/api/v1/public/*` directly using its own fetch
 *     against `apiBase` (defaults to the script-host origin).
 *   - Order completion + errors surface via the `onSubmit` / `onError`
 *     callbacks the host supplied at `mount` time.
 *
 * What's intentionally simple here: the widget embeds a minimal
 * "tenant-rebrandable address-entry + tariff CTA" — the full multi-step
 * checkout still routes through `/embed/<slug>` for hosts that need it.
 * For "drop a buy button next to a tariff" use-cases, this is enough,
 * and it keeps the bundle small (<300 KB gzip) without re-bundling the
 * entire Next checkout shell.
 */
import * as React from 'react'
import * as ReactDOM from 'react-dom/client'
import { BrandingProvider, type BrandingTheme } from '@cwflow/flow-ui'
import { WidgetCheckout } from './WidgetCheckout'

declare global {
  interface Window {
    cwflow?: CwflowGlobal
  }
}

export interface CwflowMountOptions {
  tenant: string
  brand?: string
  channel?: string
  /**
   * UI language for the widget itself (button label, placeholder, error
   * messages, no-results hint). Accepts ISO 639-1 codes — also tolerant
   * of BCP 47 tags (`"en-US"` → `"en"`) and case (`"DE"` → `"de"`).
   *
   * Currently shipped: `"de"` (default), `"en"`. Unknown codes fall back
   * to `"de"` silently. Adding more languages is a one-file change in
   * `widget/i18n.ts`.
   *
   * The active locale is also forwarded to the embed page via `?lang=`
   * on the redirect URL, so a host page that flips its language drags
   * the FLOW Bestellstrecke along — the customer doesn't see a language
   * mismatch between "buy now" button and the order form.
   *
   *   <body lang="de">  →  cwflow.mount('#h', { tenant, locale: 'de' })
   *   <body lang="en">  →  cwflow.mount('#h', { tenant, locale: 'en' })
   *
   * Operator-supplied `buttonText` / `placeholder` always win over the
   * locale defaults — explicit copy is never silently translated.
   */
  locale?: string
  apiBase?: string
  theme?: Partial<BrandingTheme['colors']>
  /**
   * Submit-button label. When set, this is rendered verbatim regardless
   * of `locale` — the host took explicit control. When omitted, the
   * widget renders the locale-appropriate default ("Tarife & Preise
   * anzeigen" for `de`, "Show plans & prices" for `en`).
   */
  buttonText?: string
  /**
   * Search-input placeholder. Same locale + override semantics as
   * `buttonText`. Defaults to the locale's full-address prompt.
   */
  placeholder?: string
  onSubmit?: (order: { orderId?: string; orderNumber?: string }) => void
  onError?: (err: Error) => void
}

export interface CwflowMountHandle {
  unmount(): void
}

export interface CwflowGlobal {
  mount(target: string | HTMLElement, options: CwflowMountOptions): CwflowMountHandle
  version: string
}

const VERSION = '0.1.0'

const SCOPED_RESET = `
/* Reset is scoped to .cwflow-root so the widget doesn't bleed onto the
   host page (and the host's resets don't bleed onto us). The container
   itself is intentionally TRANSPARENT — the partner site picks the
   surrounding background colour (the screenshot showed yellow) and the
   widget pill sits on top of it. */
.cwflow-root, .cwflow-root *, .cwflow-root *::before, .cwflow-root *::after {
  box-sizing: border-box;
}
.cwflow-root {
  font-family: var(--cw-font-body, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif);
  color: var(--cw-color-text, #0a0a0a);
  line-height: 1.4;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  background: transparent;
}
.cwflow-root button { font: inherit; cursor: pointer; }
.cwflow-root input { font: inherit; }

/* ── Bare pill bar — input + button glued together inside one rounded
      surface. Container is full-pill (border-radius: 9999) and centers
      its children vertically. The button sits flush against the right
      edge. The bar shrinks to fit the available horizontal space; on
      mobile (<480px) it stacks the button below the input.            */
.cwflow-root .cw-widget-bare {
  width: 100%;
  max-width: 720px;
  position: relative;
}
.cwflow-root .cw-widget-bar {
  display: flex;
  align-items: stretch;
  gap: 0;
  background: var(--cw-color-surface, #ffffff);
  border-radius: 9999px;
  padding: 0.4rem;
  box-shadow: 0 6px 24px -10px rgba(0, 0, 0, 0.18);
  position: relative;
}
.cwflow-root .cw-widget-bar-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0.85rem 1.25rem;
  font-size: 1rem;
  color: var(--cw-color-text, #0a0a0a);
  border-radius: 9999px;
}
.cwflow-root .cw-widget-bar-input::placeholder {
  color: #9ca3af;
}
.cwflow-root .cw-widget-bar-input:focus {
  /* Subtle focus ring inside the pill — no chunky outline that would
     break the pill silhouette. */
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--cw-color-primary-rgb, 0 131 135) / 0.18) inset;
}
.cwflow-root .cw-widget-bar-spinner {
  align-self: center;
  width: 1rem;
  height: 1rem;
  margin: 0 0.5rem;
  border: 2px solid #e5e7eb;
  border-top-color: var(--cw-color-primary, #008387);
  border-radius: 50%;
  animation: cw-spin 0.7s linear infinite;
}
@keyframes cw-spin { to { transform: rotate(360deg); } }
.cwflow-root .cw-widget-bar-btn {
  flex: 0 0 auto;
  border: none;
  border-radius: 9999px;
  padding: 0.85rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  background: var(--cw-color-text, #0a0a0a);
  color: #ffffff;
  transition: filter 0.15s ease, transform 0.15s ease, opacity 0.15s ease;
  white-space: nowrap;
}
.cwflow-root .cw-widget-bar-btn:hover:not(:disabled) {
  filter: brightness(1.08);
}
.cwflow-root .cw-widget-bar-btn:active:not(:disabled) {
  transform: translateY(1px);
}
.cwflow-root .cw-widget-bar-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* Mobile stack — button drops below the input when the bar is too
   narrow to keep them side-by-side without truncating the placeholder. */
@media (max-width: 480px) {
  .cwflow-root .cw-widget-bar {
    flex-direction: column;
    border-radius: 1.5rem;
    padding: 0.5rem;
  }
  .cwflow-root .cw-widget-bar-input {
    border-radius: 1rem;
    padding: 0.85rem 1rem;
  }
  .cwflow-root .cw-widget-bar-btn {
    border-radius: 1rem;
    padding: 0.85rem 1rem;
    margin-top: 0.4rem;
  }
}

/* ── Results dropdown — floats below the bar as its own rounded card
      (the bar is a pill, the dropdown is a softer rectangle so they
      visually relate but don't compete for the pill silhouette).      */
.cwflow-root .cw-widget-bar-results {
  list-style: none;
  margin: 0.5rem 0 0 0;
  padding: 0;
  background: var(--cw-color-surface, #ffffff);
  border-radius: 1.25rem;
  box-shadow: 0 8px 30px -10px rgba(0, 0, 0, 0.22);
  max-height: 22rem;
  overflow: auto;
  position: absolute;
  left: 0;
  right: 0;
  z-index: 50;
}
.cwflow-root .cw-widget-bar-results li {
  padding: 0.7rem 1.1rem;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
}
.cwflow-root .cw-widget-bar-results li:first-child {
  border-top-left-radius: 1.25rem;
  border-top-right-radius: 1.25rem;
}
.cwflow-root .cw-widget-bar-results li:last-child {
  border-bottom: 0;
  border-bottom-left-radius: 1.25rem;
  border-bottom-right-radius: 1.25rem;
}
.cwflow-root .cw-widget-bar-results li:hover,
.cwflow-root .cw-widget-bar-results li[aria-selected="true"] {
  background: rgba(var(--cw-color-primary-rgb, 0 131 135) / 0.08);
}
.cwflow-root .cw-widget-match-line {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--cw-color-text, #0a0a0a);
}
.cwflow-root .cw-widget-match-sub {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 1px;
}

/* ── Inline hints (no-results / error). Float same way as the
      dropdown so they don't push the layout below the widget around. */
.cwflow-root .cw-widget-bar-empty,
.cwflow-root .cw-widget-bar-error {
  margin: 0.5rem 0 0 0;
  padding: 0.7rem 1.1rem;
  border-radius: 1.25rem;
  font-size: 0.85rem;
}
.cwflow-root .cw-widget-bar-empty {
  background: rgba(255, 255, 255, 0.92);
  color: #6b7280;
  box-shadow: 0 6px 24px -10px rgba(0, 0, 0, 0.18);
}
.cwflow-root .cw-widget-bar-error {
  background: rgba(254, 226, 226, 0.95);
  color: #991b1b;
  box-shadow: 0 6px 24px -10px rgba(0, 0, 0, 0.18);
}
`

let stylesInjected = false
function ensureStyles() {
  if (stylesInjected || typeof document === 'undefined') return
  const style = document.createElement('style')
  style.id = 'cwflow-widget-styles'
  style.textContent = SCOPED_RESET
  document.head.appendChild(style)
  stylesInjected = true
}

function resolveTarget(target: string | HTMLElement): HTMLElement | null {
  if (typeof target === 'string') {
    return document.querySelector(target)
  }
  return target instanceof HTMLElement ? target : null
}

function pickApiBase(explicit?: string): string {
  if (explicit) return explicit.replace(/\/+$/, '')
  if (typeof document !== 'undefined') {
    const script = document.currentScript as HTMLScriptElement | null
    if (script?.src) {
      try {
        const u = new URL(script.src)
        return `${u.protocol}//${u.host}`
      } catch {
        /* fall through */
      }
    }
  }
  return ''
}

const cwflow: CwflowGlobal = {
  version: VERSION,
  mount(target, options) {
    if (!options.tenant) throw new Error('cwflow.mount: options.tenant is required')
    const node = resolveTarget(target)
    if (!node) throw new Error('cwflow.mount: target not found')

    ensureStyles()

    const apiBase = pickApiBase(options.apiBase)
    const root = ReactDOM.createRoot(node)
    root.render(<WidgetApp apiBase={apiBase} options={options} />)
    return {
      unmount() {
        root.unmount()
      },
    }
  },
}

interface WidgetAppProps {
  apiBase: string
  options: CwflowMountOptions
}

function WidgetApp({ apiBase, options }: WidgetAppProps) {
  const [theme, setTheme] = React.useState<BrandingTheme>({ colors: options.theme || {} })

  React.useEffect(() => {
    let cancelled = false
    fetch(`${apiBase}/api/v1/public/tenants/${encodeURIComponent(options.tenant)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.branding) return
        const next: BrandingTheme['colors'] = {
          primary: data.branding.primary_color,
          secondary: data.branding.secondary_color,
          accent: data.branding.accent_color,
          text: data.branding.text_color,
          ...(options.theme || {}),
        }
        setTheme({ colors: next })
      })
      .catch(() => {
        /* keep defaults */
      })
    return () => {
      cancelled = true
    }
  }, [apiBase, options.tenant, options.theme])

  return (
    <BrandingProvider theme={theme}>
      <WidgetCheckout apiBase={apiBase} options={options} />
    </BrandingProvider>
  )
}

if (typeof window !== 'undefined') {
  window.cwflow = cwflow
}

export default cwflow
